import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import apiService from '../services/apiService';
import { LEAD_SOURCE_OPTIONS, labelForLeadSource } from '../constants/leadSources';
import { LEAD_STATUS_OPTIONS, labelForLeadStatus } from '../constants/leadStatuses';

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  address: '',
  source: 'phone',
  status: 'new',
  notes: '',
  next_follow_up_at: '',
};

const FOLLOW_UP_FILTERS = [
  { value: '', label: 'All follow-ups' },
  { value: 'due_today', label: 'Due today' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'upcoming', label: 'Next 7 days' },
  { value: 'scheduled', label: 'Any scheduled' },
];

function formatFollowUpDate(iso) {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('D MMM YYYY') : '';
}

function followUpChipProps(iso) {
  if (!iso) return null;
  const d = dayjs(iso).startOf('day');
  const today = dayjs().startOf('day');
  if (d.isBefore(today)) return { label: `Overdue: ${formatFollowUpDate(iso)}`, color: 'error' };
  if (d.isSame(today)) return { label: `Due today`, color: 'warning' };
  return { label: `Follow-up: ${formatFollowUpDate(iso)}`, color: 'info' };
}

export default function Leads() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [followUpFilter, setFollowUpFilter] = useState(() => searchParams.get('follow_up') || '');
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [convertForm, setConvertForm] = useState({ create_client: true, create_job: true, job_name: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (followUpFilter) qs.set('follow_up', followUpFilter);
      if (search.trim()) qs.set('search', search.trim());
      qs.set('per_page', '50');
      const res = await apiService.get(`leads?${qs.toString()}`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, followUpFilter, search]);

  function setFollowUpFilterAndUrl(value) {
    setFollowUpFilter(value);
    const next = new URLSearchParams(searchParams);
    if (value) next.set('follow_up', value);
    else next.delete('follow_up');
    setSearchParams(next, { replace: true });
  }

  // Sync filter when navigating with ?follow_up= (e.g. from Dashboard chips).
  useEffect(() => {
    const urlFollowUp = searchParams.get('follow_up') || '';
    setFollowUpFilter((prev) => (prev === urlFollowUp ? prev : urlFollowUp));
  }, [searchParams]);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(lead) {
    setEditingId(lead.id);
    setForm({
      name: lead.name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      address: lead.address || '',
      source: lead.source || 'phone',
      status: lead.status || 'new',
      notes: lead.notes || '',
      next_follow_up_at: lead.next_follow_up_at ? dayjs(lead.next_follow_up_at).format('YYYY-MM-DD') : '',
    });
    setDialogOpen(true);
  }

  function openConvert(lead) {
    setConvertLead(lead);
    setConvertForm({
      create_client: !lead.client_id,
      create_job: !lead.collection_id,
      job_name: `${lead.name || 'Lead'} — job`,
    });
    setConvertOpen(true);
  }

  async function handleSave() {
    setBusy(true);
    setError('');
    try {
      const payload = {
        ...form,
        next_follow_up_at: form.next_follow_up_at?.trim() ? form.next_follow_up_at.trim() : null,
      };
      if (editingId) {
        await apiService.put(`leads/${editingId}`, payload);
      } else {
        await apiService.post('leads', payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleConvert() {
    if (!convertLead) return;
    setBusy(true);
    setError('');
    try {
      const res = await apiService.post(`leads/${convertLead.id}/convert`, convertForm);
      setConvertOpen(false);
      await load();
      if (res.collection?.id) {
        navigate('/jobs', { state: { action: 'view', jobId: res.collection.id } });
      } else if (res.client?.id) {
        navigate(`/clients?id=${res.client.id}`);
      }
    } catch (e) {
      setError(e.message || 'Convert failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Leads
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track enquiries before they become clients or jobs. Channel is the marketing source; status is your pipeline stage.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 180, flex: '1 1 180px' }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="lead-status-filter">Status</InputLabel>
          <Select
            labelId="lead-status-filter"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {LEAD_STATUS_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="lead-followup-filter">Follow-up</InputLabel>
          <Select
            labelId="lead-followup-filter"
            label="Follow-up"
            value={followUpFilter}
            onChange={(e) => setFollowUpFilterAndUrl(e.target.value)}
          >
            {FOLLOW_UP_FILTERS.map((f) => (
              <MenuItem key={f.value || 'all'} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add lead
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary">No leads match your filters.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((lead) => (
            <Card key={lead.id} variant="outlined">
              <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ flex: '1 1 240px' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {lead.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[lead.phone, lead.email].filter(Boolean).join(' · ') || 'No contact'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    <Chip size="small" label={labelForLeadStatus(lead.status)} color={lead.status === 'won' ? 'success' : lead.status === 'lost' ? 'default' : 'primary'} />
                    <Chip size="small" variant="outlined" label={labelForLeadSource(lead.source)} />
                    {lead.client_id ? (
                      <Chip size="small" component={RouterLink} to={`/clients?id=${lead.client_id}`} clickable label={`Client #${lead.client_id}`} />
                    ) : null}
                    {lead.collection_id ? (
                      <Chip size="small" clickable label={`Job #${lead.collection_id}`} onClick={() => navigate('/jobs', { state: { action: 'view', jobId: lead.collection_id } })} />
                    ) : null}
                    {(() => {
                      const fu = followUpChipProps(lead.next_follow_up_at);
                      return fu ? <Chip size="small" variant="outlined" label={fu.label} color={fu.color} /> : null;
                    })()}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" onClick={() => openEdit(lead)}>
                    Edit
                  </Button>
                  {!lead.collection_id ? (
                    <Button size="small" variant="contained" onClick={() => openConvert(lead)}>
                      Convert
                    </Button>
                  ) : null}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit lead' : 'New lead'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <TextField label="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          <TextField label="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          <TextField label="Address" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
          <FormControl>
            <InputLabel id="lead-source">Source</InputLabel>
            <Select labelId="lead-source" label="Source" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
              {LEAD_SOURCE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="lead-status">Status</InputLabel>
            <Select labelId="lead-status" label="Status" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              {LEAD_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Notes" multiline minRows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          <TextField
            label="Next follow-up"
            type="date"
            value={form.next_follow_up_at}
            onChange={(e) => setForm((f) => ({ ...f, next_follow_up_at: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            helperText="Optional reminder date for open leads"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={busy || !form.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={convertOpen} onClose={() => setConvertOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Convert lead</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Create a CRM client and/or a job from <b>{convertLead?.name}</b>.
          </Typography>
          {!convertLead?.client_id ? (
            <FormControl>
              <InputLabel id="conv-client">Client</InputLabel>
              <Select
                labelId="conv-client"
                label="Client"
                value={convertForm.create_client ? 'yes' : 'no'}
                onChange={(e) => setConvertForm((f) => ({ ...f, create_client: e.target.value === 'yes' }))}
              >
                <MenuItem value="yes">Create new client</MenuItem>
                <MenuItem value="no">Skip</MenuItem>
              </Select>
            </FormControl>
          ) : null}
          {!convertLead?.collection_id ? (
            <>
              <FormControl>
                <InputLabel id="conv-job">Job</InputLabel>
                <Select
                  labelId="conv-job"
                  label="Job"
                  value={convertForm.create_job ? 'yes' : 'no'}
                  onChange={(e) => setConvertForm((f) => ({ ...f, create_job: e.target.value === 'yes' }))}
                >
                  <MenuItem value="yes">Create job</MenuItem>
                  <MenuItem value="no">Skip</MenuItem>
                </Select>
              </FormControl>
              {convertForm.create_job ? (
                <TextField
                  label="Job name"
                  value={convertForm.job_name}
                  onChange={(e) => setConvertForm((f) => ({ ...f, job_name: e.target.value }))}
                />
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConvert} disabled={busy}>
            Convert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
